import { useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import classNames from 'classnames'

// styles
import style from '@aytm/app/stats/components/tooltip/styles/Tooltip.module.scss'

// Which side of the target the pill stands on. The tail takes the opposite edge, so `top` is a pill
// above its target with the tail underneath.
export type TooltipSide = 'top' | 'bottom' | 'left' | 'right'

// Where the pill lines up along that side: flush with the target's near edge, its middle, or its far
// edge. The tail follows the target either way -- alignment moves the pill, not what it points at.
export type TooltipAlign = 'start' | 'center' | 'end'

export type TooltipTone = 'pale' | 'deep'

// The page's tone, in one place. Reversing the pill everywhere is this word.
export const TOOLTIP_TONE: TooltipTone = 'pale'

// Clearance between the pill and what it names -- room for the tail plus a hair, so the two read as
// separate objects.
const GAP = 7
// The pill is kept this far inside the window; nothing useful can be read flush against the edge.
const EDGE = 4
// The tail stays this far from the pill's corners, where the rounding would cut it in half.
const TAIL_INSET = 12
// Beyond this the pill has been pulled so far past its target that a tail would point at nothing.
const TAIL_REACH = 24
// Past this a target is a strip rather than a mark, and pointing at the whole of it points at none of
// it -- a tooltip declared on a row's wrapper would stand at the far end of the row from the icon the
// reader is actually on. Wider than this, the pointer is the thing to point at. Exported because the
// host picks its anchor by the same measure, and the two silently stop agreeing if they drift.
export const WIDE = 240
// Half the tail's own width. The edge it is offset from is its near one, so the offset has to be the
// target's middle less this -- placed without it the tail points half its width off every time.
const TAIL_HALF = 5

export type TooltipProps = {
  text: string
  // what the pill names; it is measured, never written to
  target: HTMLElement | null
  side?: TooltipSide
  align?: TooltipAlign
  tone?: TooltipTone
  // let copy longer than a couple of words take a measure and wrap
  wrap?: boolean
  // pin the tail rather than letting it find the target: a distance along the pill's tailed edge, or
  // its middle
  tailAt?: number | 'center'
  // where the pointer entered, for the targets that are too wide to point at as a whole
  pointer?: { x: number; y: number } | null
}

type Placement = { left: number; top: number; side: TooltipSide; tailAt: number; tailless: boolean }

const isVertical = (side: TooltipSide) => 'top' == side || 'bottom' == side

// A wrapped pill takes its whole measure whichever way the lines happen to break, so a sentence that
// ends short leaves unpainted box on the right that reads as lopsided padding. The measure decides
// where the lines break; the box is then pulled in to the longest line they actually made, which is
// the only width that keeps the padding even on both sides.
function hug(pill: HTMLElement, wrap: boolean) {
  pill.style.width = ''
  if (!wrap || !pill.firstChild) return

  const range = document.createRange()
  range.selectNodeContents(pill)
  // Rects arrive per RUN, not per line: an element child -- the bold lead -- contributes its own
  // border box beside its text's, so counting rects overstates the lines and the widest run
  // understates the widest line. Runs that share vertical ground are one line, and a line's width
  // is the span of its runs together.
  const lines: { top: number; bottom: number; left: number; right: number }[] = []
  for (const run of Array.from(range.getClientRects())) {
    if (!run.width) continue
    const line = lines.find(held => run.top < held.bottom && run.bottom > held.top)
    if (line) {
      line.left = Math.min(line.left, run.left)
      line.right = Math.max(line.right, run.right)
      line.top = Math.min(line.top, run.top)
      line.bottom = Math.max(line.bottom, run.bottom)
    } else {
      lines.push({ top: run.top, bottom: run.bottom, left: run.left, right: run.right })
    }
  }
  if (lines.length < 2) return

  const widest = Math.max(...lines.map(line => line.right - line.left))
  const box = getComputedStyle(pill)
  pill.style.width = `${Math.ceil(widest) + parseFloat(box.paddingLeft) + parseFloat(box.paddingRight)}px`
}

// What the pill actually points at. Usually the mark itself; on a strip too wide to point at, the
// pointer's own column of it, kept on the strip's own line so the pill still stands off the row
// rather than over the reader's cursor.
function anchorOf(target: HTMLElement, pointer: TooltipProps['pointer']): DOMRect {
  const box = target.getBoundingClientRect()
  if (!pointer || box.width <= WIDE) return box

  return new DOMRect(pointer.x, box.top, 0, box.height)
}

// Laid out against the window rather than a container: the pill is on the body, and every surface it
// names -- a card, a table heading, a chart -- clips or scrolls its own content.
function place(target: DOMRect, pill: DOMRect, side: TooltipSide, align: TooltipAlign): Placement {
  const room = {
    top: target.top,
    bottom: window.innerHeight - target.bottom,
    left: target.left,
    right: window.innerWidth - target.right
  }
  const need = (isVertical(side) ? pill.height : pill.width) + GAP + EDGE

  // A pill that would stand outside the window takes the opposite side instead -- a mark in the
  // sticky header has nothing above it, one at the foot of the page nothing below.
  const flipped: Record<TooltipSide, TooltipSide> = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' }
  const placedSide = room[side] < need && room[flipped[side]] >= need ? flipped[side] : side

  const vertical = isVertical(placedSide)
  const measure = vertical ? pill.width : pill.height
  const centre = vertical ? target.left + target.width / 2 : target.top + target.height / 2
  const span = vertical ? { near: target.left, far: target.right } : { near: target.top, far: target.bottom }
  const limit = vertical ? window.innerWidth : window.innerHeight

  let start = 'center' == align ? centre - measure / 2 : 'end' == align ? span.far - measure : span.near

  // The tail is the constraint and the alignment gives way to it: over a mark narrower than the pill's
  // own corner, flush would push the tail into the rounding and leave it pointing beside its target.
  // Moving the pill instead keeps the tail true, and by a few pixels nobody reads as a misalignment.
  const reach = { min: TAIL_INSET, max: measure - TAIL_INSET - TAIL_HALF * 2 }
  const asked = centre - TAIL_HALF - start
  if (asked < reach.min) start -= reach.min - asked
  else if (asked > reach.max) start += asked - reach.max

  // Only the window's own edge can take the tail off the target's middle, which is the one case where
  // there is nowhere else for the pill to stand.
  start = Math.min(Math.max(EDGE, start), limit - EDGE - measure)

  const wanted = centre - TAIL_HALF - start
  const tailAt = Math.min(Math.max(reach.min, wanted), reach.max)

  const left = vertical ? start : 'left' == placedSide ? target.left - GAP - pill.width : target.right + GAP
  const top = vertical ? ('top' == placedSide ? target.top - GAP - pill.height : target.bottom + GAP) : start

  return { left, top, side: placedSide, tailAt, tailless: Math.abs(wanted - tailAt) > TAIL_REACH }
}

// A tip that opens with a short label -- "Qualifies:", "No effect:" -- says two things: what this
// is, and then what that means. The label is set in bold so the first reads at a glance and the
// sentence after it can be read or skipped. Only a genuine label qualifies: a colon inside a
// sentence, or a long run before one, is left as written. A multi-line tip is left whole too --
// those are lists of `label: figure` pairs, and emphasising only the first line's label would
// read as ranking them.
const LEAD = /^([^.:!?\n]{1,24}:)(?:[^\S\n]+)(?=\S)/

export const splitLead = (text: string) => {
  const found = text.includes('\n') ? null : LEAD.exec(text)

  return found ? { lead: found[1], rest: text.slice(found[1].length) } : { lead: '', rest: text }
}

export default function Tooltip(props: TooltipProps) {
  const ref = useRef<HTMLDivElement | null>(null)
  const [at, setAt] = useState<Placement | null>(null)

  const side = props.side ?? 'top'
  const align = props.align ?? 'start'

  // Placed after the pill has been drawn, because where it goes depends on how wide its own copy made
  // it -- the same words are one line on a wide table and three under a measure.
  useLayoutEffect(() => {
    const pill = ref.current
    if (!pill || !props.target) return setAt(null)

    // Measured from the corner, not from wherever it last stood. The pill is fixed and shrink-to-fit,
    // so its width is bounded by what is left of the window to its right: measured at a previous
    // placement near the right edge, a wrapping tip would break its lines against that sliver and be
    // pinned to the narrow tower it made.
    pill.style.left = '0px'
    pill.style.top = '0px'
    hug(pill, Boolean(props.wrap))

    const placed = place(anchorOf(props.target, props.pointer), pill.getBoundingClientRect(), side, align)
    if ('number' == typeof props.tailAt) placed.tailAt = props.tailAt
    else if ('center' == props.tailAt) {
      placed.tailAt = (isVertical(placed.side) ? pill.offsetWidth : pill.offsetHeight) / 2
    }
    if (props.tailAt !== undefined) placed.tailless = false

    // Written here rather than through the render below. The measurement above moves the pill to the
    // corner, and React only rewrites an inline value its own previous render disagrees with -- so a
    // pill that lands at the same left twice running keeps the corner it was measured from, while its
    // top, which does change from row to row, follows correctly. Every mark in a column of them is
    // exactly that case.
    pill.style.left = `${placed.left}px`
    pill.style.top = `${placed.top}px`

    setAt(placed)
  }, [props.target, props.text, props.tailAt, props.wrap, props.pointer, side, align])

  const { lead, rest } = splitLead(props.text)

  return createPortal(
    <div
      ref={ref}
      className={classNames(style.component, style[`side-${at?.side ?? side}`], {
        [style['tone-deep']]: 'deep' == (props.tone ?? TOOLTIP_TONE),
        [style.wrapping]: props.wrap,
        [style.tailless]: at?.tailless
      })}
      role="tooltip"
      dangerouslySetInnerHTML={{
        __html: lead ? `<b>${lead}</b> ${rest}` : props.text
      }}
      style={
        {
          '--tail-at': `${at?.tailAt ?? TAIL_INSET}px`,
          // measured before it is seen: drawn where it will finally stand, it would be read in the
          // wrong place for a frame
          visibility: at ? 'visible' : 'hidden'
        } as React.CSSProperties
      }
    />,
    document.body
  )
}
